<template>
    <div class="top-section">
        <span class="text">
            <span
                v-if="isBackgroundLoading"
                v-html="
                    $trans('web_search.founded_candidates_plural').replace(
                        ':number',
                        `<span class='inline-spinner'></span>`
                    )
                "
            ></span>
            <span v-else>
                {{
                    countOfProfiles() === 1
                        ? $trans('web_search.founded_candidates_singular').replace(':number', countOfProfiles())
                        : $trans('web_search.founded_candidates_plural').replace(':number', countOfProfiles())
                }}
            </span>
            <el-popover
                v-model:visible="infoVisible"
                trigger="manual"
                placement="top"
                popper-class="info-popover"
                :show-arrow="false"
                :popper-style="{
                    backgroundColor: 'white',
                    width: '400px',
                    height: '450px',
                    boxShadow: '4px 4px 12px #00000043',
                    border: '2px solid #41ABC4',
                    borderRadius: '13px',
                }"
            >
                <template #reference>
                    <button class="info-icon" @click.stop="toggleInfo">
                        𝒊
                    </button>
                </template>

                <div class="popover-content">
                    <div class="popover-header">
                        <div class="info-icon-large">𝒊</div>
                    </div>
                    <h3 class="popover-title">
                        {{ $trans('web_search.profile_list.search_info_title') }}
                    </h3>
                    <div class="info-content">
                        <p>
                            {{ $trans('web_search.profile_list.search_info_text') }}
                        </p>
                    </div>
                    <button class="close-btn" @click="infoVisible = false">
                        ✕
                    </button>
                </div>
            </el-popover>
        </span>

        <div class="buttons-container">
            <button
                class="btn add-to-board-btn"
                @click="toggleSelectAllCandidates"
            >
                {{ $trans('web_search.profile_list.save_all_candidates') }}
                <img
                    src="/media/images/web_search/clipboard-plus.svg"
                    alt="icon"
                    class="icon"
                />
            </button>
        </div>
    </div>

    <div class="wrapper">
        <div class="filters-section">
            <Filters
                ref="filtersComponent"
                :filters="filters"
                :isOpen="true"
                @closeFilters="mobileFilterState = false"
            />
        </div>
        <div class="cards-section">
            <TransitionGroup name="bounce">
                <ProfileCard
                    v-for="profile in getProfiles()"
                    :profile="profile"
                    :key="profile.id"
                    ref="items"
                    :data-profile-id="profile.id"
                    :main-filters="mainFilters"
                    :group-name="groupName"
                    @showConfirmationModal="
                        (profile) => $emit('showConfirmationModal', profile)
                    "
                />
            </TransitionGroup>
        </div>
    </div>
</template>

<script>
import ProfileCard from "./Profile/ProfileCard";
import { useCandidateBoardStore } from "@/stores/CandidateBoardStore";
import { mapStores } from "pinia";
import Filters from "./Filters";
import Indicator from "./Indicator";

export default {
    components: {
        ProfileCard,
        Filters,
        Indicator,
    },
    props: {
        totalCount: {
            type: Number,
            required: false,
            default: 0,
        },
        isBackgroundLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
        profiles: {
            type: Array,
            required: false,
        },
        tmpProfiles: {
            type: Array,
            required: false,
        },
        page: {
            type: Number,
            required: false,
        },
        filters: {
            type: Array,
            required: true,
        },
        mainFilters: {
            type: Array,
            required: true,
        },
        groupName: String,
    },
    data() {
        return {
            currentPage: 1,
            profileList: [],
            mobileFilterState: false,
            infoVisible: false,
        };
    },
    created() {
        const elMain = document.querySelector(".el-main");
        elMain.addEventListener("scroll", this.handleScroll);
    },
    unmounted() {
        const elMain = document.querySelector(".el-main");
        elMain.removeEventListener("scroll", this.handleScroll);
    },
    computed: {
        ...mapStores(useCandidateBoardStore),
        allSelected() {
            return this.tmpProfiles.every((profile) =>
                this.CandidateBoardStore.isAddedToBoard(profile.id),
            );
        },
    },
    methods: {
        toggleInfo() {
            this.infoVisible = !this.infoVisible;
        },
        handleScroll() {
            const elMain = document.querySelector(".el-main");
            if (!elMain) return;

            if (
                elMain.scrollHeight - elMain.scrollTop <=
                elMain.clientHeight + 800
            ) {
                this.currentPage++;
            }
        },
        getProfiles() {
            return this.tmpProfiles.slice(0, this.currentPage * 10);
        },
        resetPage() {
            this.currentPage = 1;
        },
        countOfProfiles() {
            return this.totalCount > 0
                ? this.totalCount
                : this.tmpProfiles.length;
        },
        toggleSelectAllCandidates() {
            if (this.allSelected) {
                this.tmpProfiles.forEach((profile) =>
                    this.CandidateBoardStore.removeFromBoard(profile.id),
                );
            } else {
                this.tmpProfiles.forEach((profile) => {
                    if (!this.CandidateBoardStore.isAddedToBoard(profile.id)) {
                        this.CandidateBoardStore.addToBoard(profile);
                    }
                });
            }
        },
        toggleFilterState() {
            this.mobileFilterState = !this.mobileFilterState;
        },
        activeFiltersCount() {
            if (this.$refs.filtersComponent) {
                return this.$refs.filtersComponent.activeFiltersCount();
            } else {
                return 0;
            }
        },
    },
};
</script>

<style scoped>
:deep(.inline-spinner) {
    display: inline-block;
    width: 22px;
    height: 22px;
    border: 3px solid rgba(65, 171, 196, 0.3);
    border-radius: 50%;
    border-top-color: #41abc4;
    animation: spin 1s ease-in-out infinite;
    vertical-align: middle;
    margin: 0 5px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.top-section {
    margin-top: 10px;
    padding-top: 10px;
    padding-bottom: 10px;
    margin-bottom: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: flex-start;
    color: #000;
    width: 79%;
    left: 10px;
    margin-left: 10%;
    justify-content: space-between;
}

.wrapper {
    margin-top: 10px;
    padding-top: 10px;
    padding-bottom: 10px;
    margin-bottom: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
    align-items: flex-start;
    margin-left: 10%;
}

.text {
    text-align: left;
    font: normal 400 30px/36px Barlow;
    letter-spacing: -0.3px;
    color: #1c1c1c;
    opacity: 1;
}

.buttons-container {
    display: flex;
    gap: 10px;
}

.btn {
    display: inline-flex;
    align-items: center;
    background-color: transparent;
    color: #343433;
    border: 1px solid #707070;
    border-radius: 20px;
    padding: 8px 15px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;

    text-align: right;
    font: italic 500 18px/22px Barlow;
    letter-spacing: -0.27px;
    color: #343433;
    opacity: 1;
}

.btn:hover {
    background-color: #fff;
}

.btn.filter-btn:hover img {
    filter: brightness(0) saturate(100%) invert(62%) sepia(46%) saturate(584%)
        hue-rotate(144deg) brightness(90%) contrast(82%);
}

.btn.filter-btn:hover {
    color: #41abc4;
}

.icon {
    width: 22px;
    height: 19px;
    margin-left: 8px;
}

.add-to-board-btn .icon {
    opacity: 0.5;
    width: 24px;
    height: 22px;
    margin-left: 10px;
    margin-top: -3px;
    filter: brightness(0) saturate(100%) invert(0%) sepia(76%) saturate(15%)
        hue-rotate(339deg) brightness(99%) contrast(100%);
}

.add-to-board-btn:hover .icon {
    filter: brightness(0) saturate(100%) invert(62%) sepia(46%) saturate(584%)
        hue-rotate(144deg) brightness(90%) contrast(82%);
}

.add-to-board-btn:hover {
    color: #41abc4;
}

.indicator {
    left: 20px;
}

.filters-section {
    flex: 0 0 400px;
}

.cards-section {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;
}

.info-icon {
    width: 27px;
    height: 27px;
    background-color: #b1b1b1;
    border: none;
    border-radius: 50%;
    font-size: 29px;
    font-weight: bold;
    cursor: pointer;
    text-align: center;
    line-height: 22px;
    margin-left: 16px;
    padding: unset;
    font-family: "KaTeX Math", serif;
    color: #fff;
    margin-left: 4px;
}

.info-icon:hover {
    background-color: #bbb;
}

:deep(.info-popover) {
    height: 300px !important;
}

.el-popover :deep(.el-popper__arrow) {
    display: none !important;
}

:deep(.el-popover__title) {
    text-align: center;
}

.info-content {
    position: relative;
}

.info-content p {
    letter-spacing: -0.36px;
    color: #3c3c3c;
    font: normal normal normal 18px/32px Inter;
    margin-top: -17px;
}

.info-icon:hover {
    background-color: #bbb;
}

.popover-header {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 60px;
}

.info-icon-large {
    width: 35px;
    height: 35px;
    background-color: #41abc4;
    color: white;
    border-radius: 50%;
    font-size: 24px;
    font-weight: bold;
    text-align: center;
    line-height: 35px;
    font-family: "KaTeX Math", serif;
}

.popover-title {
    text-align: center;
    font: italic normal 500 32px/50px Cormorant Garamond;
    letter-spacing: -0.96px;
    color: #4d5255;
    opacity: 1;
}

.info-content {
    font-size: 14px;
    color: #333;
    text-align: center;
}

.close-btn {
    position: absolute;
    top: 6px;
    right: 14px;
    background: none;
    border: none;
    font-size: 30px;
    cursor: pointer;
    color: #707070;
}

@media only screen and (max-width: 790px) {
    .wrapper {
        justify-content: center;
        margin-left: unset !important;
    }

    .search-result {
        border-top: unset;
        width: 100%;
    }

    .top-section {
        border-top: unset;
        width: 100%;
        margin-left: unset;
    }
}

.bounce-enter-active {
    animation: bounce-in 0.6s;
}

.bounce-leave-active {
    animation: bounce-in 0.6s reverse;
}

@keyframes bounce-in {
    0% {
        transform: scale(0);
    }

    50% {
        transform: scale(1.05);
    }

    100% {
        transform: scale(1);
    }
}
</style>